import React, {useEffect} from "react"
import { connect } from "react-redux"
import {
  generateCurrentCompanyBalanceSheetReport,
  generateGoodwillReport,
} from "../../../../../store/modules/financials/actions";
import FinancialAccount from "../../../../../models/FinancialAccount"
import {Divider} from "@mui/material";


const {
  currentAssets,
  longTermAssets,
  currentLiabilities,
  longTermLiabilities
} = FinancialAccount.getAllowedBalanceSheetItemGroups()


function GoodwillApplicationBalanceSheet({
  currentCompanyBalanceSheetReport,
  currentCompanyBalanceSheetReportSummary,
  currentGoodwillReport,
  dispatchGenerateCurrentCompanyBalanceSheetReport,
  dispatchGenerateGoodwillReport
}) {
  // Mounted
  useEffect(() => {
    dispatchGenerateCurrentCompanyBalanceSheetReport()
    dispatchGenerateGoodwillReport()
  }, [])

  // Methods
  const hasCategories = () => {
    return currentCompanyBalanceSheetReport.some(report => report.categories.length > 0)
  }

  return (
    <>
      <div className="inline-block w-full max-w-xl border border-neutral-200 p-10 box-content mr-3">
        {hasCategories() && (
          <div className="flex justify-center text-xl font-bold w-full mb-8">Assets</div>
        )}
        <div className="mt-5">
          {hasCategories() ? (
            currentCompanyBalanceSheetReport?.filter((group) => [
              currentAssets,
              longTermAssets
            ].includes(group.groupName)).map((data, dataIndex) => (
              <div key={dataIndex} className="mt-5">
                <div className={dataIndex > 0 && data?.categories?.length > 0 ? "mt-10" : null}>
                  {data?.categories?.map((category, categoryIndex) => (
                    <div key={categoryIndex} className="flex items-center mb-2">
                      <div className="w-full flex items-center pr-2">
                        <label className="whitespace-nowrap text-center">{category.categoryName}</label>
                      </div>
                      <div className="w-full justify-end flex items-center pr-2">
                        <label className="whitespace-nowrap">{category.adjustedValueFormatted}</label>
                      </div>
                    </div>
                  ))}
                  {data?.categories?.length > 0 && (
                    <div className="mt-5">
                      <div className="flex items-center mb-4 bg-neutral-100 p-5 rounded-lg">
                        <div className="w-full flex items-center">
                          <label className="whitespace-nowrap font-semibold">Total {data?.groupName}</label>
                        </div>
                        <div className="w-full justify-end flex items-center -ml-5">
                          <label className="whitespace-nowrap font-semibold">{data?.totalAdjustedValue}</label>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))) : null}

          <div className="mt-5">
            <div className="flex items-center mb-4 bg-neutral-200 p-5 rounded-lg">
              <div className="w-full flex items-center">
                <label className="whitespace-nowrap font-semibold">Total Tangible Assets</label>
              </div>
              <div className="w-full justify-end flex items-center -ml-5">
                <label className="whitespace-nowrap font-semibold">
                  {currentCompanyBalanceSheetReportSummary?.assets?.totalAdjustedValueFormatted}
                </label>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-7">
          <Divider />
          <div className="py-5">
            <div>
              <div className="flex items-center mb-4 bg-neutral-100 p-5 rounded-lg">
                <div className="w-full flex items-center">
                  <label className="whitespace-nowrap font-semibold">Goodwill</label>
                </div>
                <div className="w-full justify-end flex items-center -ml-5">
                  <label className="whitespace-nowrap font-semibold">
                    {currentGoodwillReport?.formattedGoodwillAmount}
                  </label>
                </div>
              </div>
            </div>
            <div>
              <div className="flex items-center mb-4 bg-neutral-100 p-5 rounded-lg">
                <div className="w-full flex items-center">
                  <label className="whitespace-nowrap font-semibold">Goodwill % of Assets</label>
                </div>
                <div className="w-full justify-end flex items-center -ml-5">
                  <label className="whitespace-nowrap font-semibold">
                    {currentGoodwillReport?.formattedGoodwillPercentageOfAssets}
                  </label>
                </div>
              </div>
            </div>
            <div>
              <div className="flex items-center bg-neutral-100 p-5 rounded-lg">
                <div className="w-full flex items-center">
                  <label className="whitespace-nowrap font-semibold">Goodwill % of Equity</label>
                </div>
                <div className="w-full justify-end flex items-center -ml-5">
                  <label className="whitespace-nowrap font-semibold">
                    {currentGoodwillReport?.formattedGoodwillPercentageOfEquity}
                  </label>
                </div>
              </div>
            </div>
          </div>
          <Divider />
          <div className="mt-10">
            <div className="flex items-center mb-4 bg-neutral-200 p-5 rounded-lg">
              <div className="w-full flex items-center">
                <label className="whitespace-nowrap font-semibold">Total Assets</label>
              </div>
              <div className="w-full justify-end flex items-center -ml-5">
                <label className="whitespace-nowrap font-semibold">
                  {currentGoodwillReport?.formattedTotalAssets}
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="inline-block w-full max-w-xl border border-neutral-200 p-10 box-content">
        <div>
          {hasCategories() && (
            <div className="flex justify-center text-xl font-bold w-full mb-8">Liabilities</div>
          )}

          {hasCategories() ? (
            currentCompanyBalanceSheetReport?.filter((group) => [
              currentLiabilities,
              longTermLiabilities
            ].includes(group.groupName)).map((data, dataIndex) => (
              <div key={dataIndex} className="mt-5">
                <div className={dataIndex > 0 && data?.categories?.length > 0 ? "mt-10" : null}>
                  {data?.categories?.map((category, categoryIndex) => (
                    <div key={categoryIndex} className="flex items-center mb-2">
                      <div className="w-full flex items-center pr-2">
                        <label className="whitespace-nowrap text-center">{category.categoryName}</label>
                      </div>
                      <div className="w-full justify-end flex items-center pr-2">
                        <label className="whitespace-nowrap">{category.adjustedValueFormatted}</label>
                      </div>
                    </div>
                  ))}
                  {data?.categories?.length > 0 && (
                    <div className="mt-5">
                      <div className="flex items-center mb-4 bg-neutral-100 p-5 rounded-lg">
                        <div className="w-full flex items-center">
                          <label className="whitespace-nowrap font-semibold">Total {data?.groupName}</label>
                        </div>
                        <div className="w-full justify-end flex items-center -ml-5">
                          <label className="whitespace-nowrap font-semibold">{data?.totalAdjustedValue}</label>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))) : null}

          <div className="mt-5">
            <div className="flex items-center mb-4 bg-neutral-200 p-5 rounded-lg">
              <div className="w-full flex items-center">
                <label className="whitespace-nowrap font-semibold">Total Liabilities</label>
              </div>
              <div className="w-full justify-end flex items-center -ml-5">
                <label className="whitespace-nowrap font-semibold">
                  {currentCompanyBalanceSheetReportSummary?.liabilities?.totalAdjustedValueFormatted}
                </label>
              </div>
            </div>
          </div>
          
        </div>

        <div className="mt-20">
          {hasCategories() && (
            <div className="flex justify-center text-xl font-bold w-full mb-8">Equity Value</div>
          )}
          <div className="mt-5">
            <div className="mt-10">
                <div className="flex items-center mb-2 bg-neutral-100 p-5 rounded-lg">
                  <div className="w-full flex items-center pr-2">
                    <label className="whitespace-nowrap text-center">Total Assets</label>
                  </div>
                  <div className="w-full justify-end flex items-center pr-2">
                    <label className="whitespace-nowrap">
                      {currentGoodwillReport?.formattedTotalAssets}
                    </label>
                  </div>
                </div>
              <div className="flex items-center mb-2 bg-neutral-100 p-5 rounded-lg">
                <div className="w-full flex items-center pr-2">
                  <label className="whitespace-nowrap text-center">Total Liabilities</label>
                </div>
                <div className="w-full justify-end flex items-center pr-2">
                  <label className="whitespace-nowrap">
                    {currentGoodwillReport?.formattedTotalLiabilities}
                  </label>
                </div>
              </div>
                <div className="mt-5">
                  <div className="flex items-center mb-4 bg-neutral-200 p-5 rounded-lg">
                    <div className="w-full flex items-center">
                      <label className="whitespace-nowrap font-semibold">Equity Value of Business</label>
                    </div>
                    <div className="w-full justify-end flex items-center -ml-5">
                      <label className="whitespace-nowrap font-semibold">
                        {currentGoodwillReport?.formattedEquityValueOfBusiness}
                      </label>
                    </div>
                  </div>
                </div>
            </div>
          </div>
        </div>

      </div>
    </>
  )
}

const mapStateToProps = (state) => ({
  currentCompanyBalanceSheetReport: state.financials.currentCompanyBalanceSheetReport,
  currentCompanyBalanceSheetReportSummary: state.financials.currentCompanyBalanceSheetReportSummary,
  currentGoodwillReport: state.financials.currentGoodwillReport
})

const mapDispatchToProps = (dispatch) => ({
  dispatchGenerateCurrentCompanyBalanceSheetReport() {
    dispatch(generateCurrentCompanyBalanceSheetReport())
  },

  dispatchGenerateGoodwillReport() {
    dispatch(generateGoodwillReport())
  }
})

export default connect(mapStateToProps, mapDispatchToProps)(GoodwillApplicationBalanceSheet)